import React, { useEffect, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  Modal,
  ModalHeader,
  Row,
  Col,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import { FaEdit, FaPlus, FaEye, FaTrashAlt, FaInfoCircle } from "react-icons/fa"
import Configs from "../../config"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Loader from "components/Loader/Loader"
import {
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Popover,
  PopoverBody,
} from "reactstrap"
import classnames from "classnames"
import Swal from "sweetalert2"
import { MdAdd, MdCancel, MdDelete, MdSave } from "react-icons/md"
import moment from "moment"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const getNearestNextFollowUpDate = (followUps = []) => {
  if (!followUps.length) return "-"

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const upcoming = followUps
    .filter(f => f.nextFollowUpDate)
    .map(f => ({
      ...f,
      nextDateObj: new Date(f.nextFollowUpDate),
    }))
    .filter(f => f.nextDateObj >= today)
    .sort((a, b) => a.nextDateObj - b.nextDateObj)

  if (!upcoming.length) return "-"

  return moment(upcoming[0].nextFollowUpDate).format("DD-MM-YYYY")
}

const FILTER_KEY = "leadsFilters"

const getStoredFilter = (key, defaultVal) => {
  try {
    const saved = sessionStorage.getItem(FILTER_KEY)
    if (saved) return JSON.parse(saved)[key] ?? defaultVal
  } catch {}
  return defaultVal
}

const LeadsIndex = () => {
  document.title = "Leads | Vintage Infra Equipment Sol PVT LTD"
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [leads, setLeads] = useState([])
  const [employeeList, setemployeeList] = useState([])

  const [followUpModal, setFollowUpModal] = useState(false)
  const [activeTab, setActiveTab] = useState("add")
  const [selectedLeadId, setSelectedLeadId] = useState(null)

  const [dateFrom, setDateFrom] = useState(() => getStoredFilter("dateFrom", ""))
  const [dateTo, setDateTo] = useState(() => getStoredFilter("dateTo", ""))
  const [employeeFilter, setEmployeeFilter] = useState(() => getStoredFilter("employeeFilter", ""))
  const [leadTypeFilter, setLeadTypeFilter] = useState(() => getStoredFilter("leadTypeFilter", ""))
  const [datePreset, setDatePreset] = useState(() => getStoredFilter("datePreset", ""))
  const [searchTerm, setSearchTerm] = useState(() => getStoredFilter("searchTerm", ""))
  const [currentPage, setCurrentPage] = useState(() => getStoredFilter("currentPage", 1))
  const [pageSize, setPageSize] = useState(() => getStoredFilter("pageSize", 20))

  const [followUpForm, setFollowUpForm] = useState({
    date: "",
    nextFollowUpDate: "",
    description: "",
  })

  const [followUpHistory, setFollowUpHistory] = useState([])

  const [openPopoverId, setOpenPopoverId] = useState(null)

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({ dateFrom, dateTo, employeeFilter, leadTypeFilter, datePreset, searchTerm, currentPage, pageSize })
    )
  }, [dateFrom, dateTo, employeeFilter, leadTypeFilter, datePreset, searchTerm, currentPage, pageSize])

  useEffect(() => {
    fetchLeads()
    fetchemployees()

    // Clear stored filters only when navigating away from any leads page
    return () => {
      if (!window.location.pathname.includes("leads")) {
        sessionStorage.removeItem(FILTER_KEY)
      }
    }
  }, [])

  async function fetchLeads() {
    const APIdata = await fetchData(setLoading, "leads")
    if (APIdata?.success) {
      setLeads(APIdata?.data)
    }
  }

  async function fetchemployees() {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) {
      setemployeeList(APIdata?.data)
    }
  }

  const applyPreset = preset => {
    setDatePreset(preset)
    setCurrentPage(1)
    const today = new Date()
    const fmt = d => d.toISOString().split("T")[0]

    const presetMap = {
      today: [fmt(today), fmt(today)],
      yesterday: (() => {
        const d = new Date(today)
        d.setDate(d.getDate() - 1)
        return [fmt(d), fmt(d)]
      })(),
      last_week: (() => {
        const s = new Date(today)
        s.setDate(s.getDate() - 7)
        return [fmt(s), fmt(today)]
      })(),
      last_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 1)
        return [fmt(s), fmt(today)]
      })(),
      last_3_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 3)
        return [fmt(s), fmt(today)]
      })(),
      last_6_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 6)
        return [fmt(s), fmt(today)]
      })(),
      last_1_year: (() => {
        const s = new Date(today)
        s.setFullYear(s.getFullYear() - 1)
        return [fmt(s), fmt(today)]
      })(),
    }

    if (preset === "") {
      setDateFrom("")
      setDateTo("")
    } else {
      const [from, to] = presetMap[preset]
      setDateFrom(from)
      setDateTo(to)
    }
  }

  const filteredLeads = leads.filter(lead => {
    // 🔹 Search filter
    const matchesSearch = Object.values({
      company: lead.company,
      contact: lead.contactPersonName,
      email: lead.contactPersonEmail,
      phone: lead.contactPersonPhone,
      source: lead.source,
      leadType: lead.leadType,
      status: lead.status,
      assignedStaff: lead.assignedStaff,
    })
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())

    // 🔹 Badge filter
    const matchesLeadType = !leadTypeFilter || lead.leadType === leadTypeFilter

    const matchesEmployee =
      !employeeFilter || lead?.assignedTo === Number(employeeFilter)

    let matchesDate = true
    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null
      const to = dateTo ? new Date(dateTo) : null
      if (from) from.setHours(0, 0, 0, 0)
      if (to) to.setHours(23, 59, 59, 999)

      // 🔹 Check createdAt
      const createdAt = lead?.createdAt ? new Date(lead.createdAt) : null
      const matchesCreatedAt =
        createdAt && (!from || createdAt >= from) && (!to || createdAt <= to)

      // 🔹 Check nearest upcoming next follow-up date
      const nextFollowUp = lead?.followUps
        ?.filter(f => f.nextFollowUpDate)
        .map(f => new Date(f.nextFollowUpDate))
        .filter(d => !isNaN(d))
        .sort((a, b) => a - b)[0]

      const matchesNextFollowUp =
        nextFollowUp &&
        (!from || nextFollowUp >= from) &&
        (!to || nextFollowUp <= to)

      // ✅ Pass if EITHER createdAt OR nextFollowUpDate falls in range
      matchesDate = matchesCreatedAt || matchesNextFollowUp
    }

    return matchesSearch && matchesLeadType && matchesEmployee && matchesDate
  })

  /* ================= Pagination ================= */
  const totalPages = Math.ceil(filteredLeads.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedLeads = filteredLeads.slice(start, start + pageSize)

  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split("T")[0] // YYYY-MM-DD
  }

  const handleFollowUp = async lead => {
    setSelectedLeadId(lead?.id)
    setActiveTab("add")
    setFollowUpHistory(lead?.followUps || [])
    setFollowUpForm({
      date: getTodayDate(),
      nextFollowUpDate: "",
      description: "",
    })
    setFollowUpModal(true)
  }

  const handleFollowUpChange = e => {
    setFollowUpForm({
      ...followUpForm,
      [e.target.name]: e.target.value,
    })
  }

  const saveFollowUp = async () => {
    if (!followUpForm.date) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select Follow Up Date",
      })
      return
    }

    if (!followUpForm.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please enter Description",
      })
      return
    }

    if (
      followUpForm.nextFollowUpDate &&
      followUpForm.nextFollowUpDate < followUpForm.date
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Next Follow Up Date cannot be before Follow Up Date",
      })
      return
    }

    const payload = {
      ...followUpForm,
      leadId: selectedLeadId,
    }

    if (!payload.nextFollowUpDate) {
      delete payload.nextFollowUpDate
    }

    const APIdata = await FetchPostData(setLoading, "leads/follow/up", payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "FollowUp Added Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })

      setFollowUpHistory(APIdata?.data?.data)

      setFollowUpForm({
        date: "",
        nextFollowUpDate: "",
        description: "",
      })

      setActiveTab("history")
      await fetchLeads()
    } else {
      Swal.fire({
        title: "Error Adding FollowUp",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const deleteFollowUp = async id => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This follow-up will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    const APIdata = await FetchDeleteData(setLoading, "leads/follow/up", id)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Deleted!",
        text: APIdata?.data?.message || "Follow-up deleted successfully.",
        icon: "success",
      })
      setFollowUpHistory(APIdata?.data?.data)
    } else {
      Swal.fire({
        title: "Error",
        text: APIdata?.response?.data?.message || "Failed to delete follow-up.",
        icon: "error",
      })
    }
  }

  const handleStatusChange = async (id, value) => {
    const APIdata = await FetchPutData(setLoading, "leads", id, {
      status: value,
    })

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Lead Status Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      fetchLeads()
    } else {
      Swal.fire({
        title: "Error Updating Lead Status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleLeadChange = async (id, value) => {
    const APIdata = await FetchPutData(setLoading, "leads", id, {
      leadType: value,
    })

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Lead Type Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      fetchLeads()
    } else {
      Swal.fire({
        title: "Error Updating Lead Type",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }
  const { checkAccess } = usePermissionData()

  const [selectedLeadDiscussion, setSelectedLeadDiscussion] = useState("")
  const [disussionModal, setDisussionModal] = useState(false)

  return (
    <PermittedComponent isAccess={checkAccess(2, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Leads Management" breadcrumbItem="Lead" />

        <Card>
          <CardBody>
            {/* ================= Top Actions ================= */}
            <div className="d-flex justify-content-between mb-3">
              <div className="d-flex align-items-center gap-3">
                <Input
                  type="select"
                  className="rounded-4"
                  value={pageSize}
                  style={{ width: 90 }}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                <Input
                  placeholder="Search leads..."
                  className="rounded-4"
                  style={{ width: 250 }}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                />

                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold">From</small>
                  <Input
                    type="date"
                    className="rounded-4"
                    style={{ width: 150 }}
                    value={dateFrom}
                    onChange={e => {
                      setDateFrom(e.target.value)
                      setDatePreset("")
                      setCurrentPage(1)
                    }}
                  />
                </div>

                {/* Date To */}
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold">To</small>
                  <Input
                    type="date"
                    className="rounded-4"
                    style={{ width: 150 }}
                    value={dateTo}
                    onChange={e => {
                      setDateTo(e.target.value)
                      setDatePreset("")
                      setCurrentPage(1)
                    }}
                  />
                </div>

                {/* Employee filter */}
                <Input
                  type="select"
                  className="rounded-4"
                  style={{ width: 180 }}
                  value={employeeFilter}
                  onChange={e => {
                    setEmployeeFilter(e.target.value)

                    setCurrentPage(1)
                  }}
                >
                  <option value="">All Employees</option>
                  {employeeList.map(name => (
                    <option key={name?.id} value={name?.id}>
                      {name?.name}
                    </option>
                  ))}
                </Input>

                <Input
                  type="select"
                  className="rounded-4"
                  style={{ width: 160 }}
                  value={datePreset}
                  onChange={e => applyPreset(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_3_month">Last 3 Months</option>
                  <option value="last_6_month">Last 6 Months</option>
                  <option value="last_1_year">Last 1 Year</option>
                </Input>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <Badge
                  color="danger"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("hot_1_month")
                    setCurrentPage(1)
                  }}
                >
                  Hot: {leads.filter(i => i.leadType === "hot_1_month").length}
                </Badge>

                <Badge
                  color="warning"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("warm_2_month")
                    setCurrentPage(1)
                  }}
                >
                  Warm:{" "}
                  {leads.filter(i => i.leadType === "warm_2_month").length}
                </Badge>

                <Badge
                  color="info"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("cold_3_month")
                    setCurrentPage(1)
                  }}
                >
                  Cold:{" "}
                  {leads.filter(i => i.leadType === "cold_3_month").length}
                </Badge>

                <Badge
                  color="secondary"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("normal_enquiry")
                    setCurrentPage(1)
                  }}
                >
                  Normal:{" "}
                  {leads.filter(i => i.leadType === "normal_enquiry").length}
                </Badge>

                <Badge
                  color="success"
                  role="button"
                  className="p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("extreme_hot_10_days")
                    setCurrentPage(1)
                  }}
                >
                  Extremely Hot:{" "}
                  {
                    leads.filter(i => i.leadType === "extreme_hot_10_days")
                      .length
                  }
                </Badge>
                {leadTypeFilter && (
                  <Button
                    color="dark"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setLeadTypeFilter("")
                      setCurrentPage(1)
                    }}
                  >
                    <MdCancel />
                  </Button>
                )}
              </div>
              {checkAccess(2, "add") && (
                <Button
                  className="btn-blue rounded-4 px-3"
                  onClick={() => navigate(`${Configs.appurl}leads/add`)}
                >
                  + Lead
                </Button>
              )}
            </div>

            {/* ================= Table ================= */}
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Contact Person</th>
                  <th>Phone</th>
                  <th>Source</th>
                  <th>Equipment</th>
                  <th>Lead Type</th>
                  <th>Assigned</th>
                  <th>Next Follow Up</th>
                  <th style={{ minWidth: "12%" }}>Action</th>
                  <th>Discussion</th>
                </tr>
              </thead>

              <tbody>
                {paginatedLeads.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No leads found
                    </td>
                  </tr>
                )}

                {paginatedLeads.map((lead, index) => (
                  <tr key={lead.id}>
                    <td>{start + index + 1}</td>
                    <td>{moment(lead.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      {lead.contactPersonName}{" "}
                      <span
                        id={`company-info-${lead.id}`}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setOpenPopoverId(
                            openPopoverId === lead.id ? null : lead.id
                          )
                        }
                      >
                        <FaInfoCircle className="ms-2" />
                      </span>
                      <Popover
                        placement="bottom"
                        isOpen={openPopoverId === lead.id}
                        target={`company-info-${lead.id}`}
                        toggle={() => setOpenPopoverId(null)}
                      >
                        <PopoverBody className="bg-dark text-white rounded-3">
                          {lead?.company || "N/A"}
                        </PopoverBody>
                      </Popover>
                    </td>
                    <td>{lead.contactPersonPhone}</td>
                    <td>{lead.leadSource?.sourceName || "--"}</td>
                    <td>{lead?.equipment_category?.name || "--"}</td>
                    <td>
                      <Input
                        type="select"
                        name="leadType"
                        value={lead.leadType || ""}
                        onChange={e => {
                          handleLeadChange(lead?.id, e.target.value)
                        }}
                        className="form-select"
                        style={{ width: "150px" }}
                      >
                        <option value="">Select</option>
                        <option value="hot_1_month">Hot (1 Month)</option>
                        <option value="warm_2_month">Warm (2 Month)</option>
                        <option value="cold_3_month">Cold (3 Month)</option>
                        <option value="normal_enquiry">Normal (Enquiry)</option>
                        <option value="extreme_hot_10_days">
                          Extreme Hot (10 days)
                        </option>
                      </Input>
                    </td>

                    <td>{lead?.user?.name}</td>
                    <td>{getNearestNextFollowUpDate(lead?.followUps)}</td>

                    <td>
                      <div style={{ width: "100px" }}>
                        <Button
                          title="View"
                          size="sm"
                          color="secondary"
                          className="me-1"
                          onClick={() =>
                            navigate(`${Configs.appurl}leads/view/${lead.id}`)
                          }
                        >
                          <FaEye />
                        </Button>

                        {checkAccess(2, "edit") && (
                          <Button
                            size="sm"
                            color="success"
                            title="Edit"
                            className="me-1"
                            onClick={() =>
                              navigate(`${Configs.appurl}leads/edit/${lead.id}`)
                            }
                          >
                            <FaEdit />
                          </Button>
                        )}
                        {checkAccess(2, "edit") && (
                          <Button
                            size="sm"
                            color="primary"
                            title="Follow Up"
                            onClick={() => {
                              handleFollowUp(lead)
                            }}
                          >
                            <FaPlus />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>
                      <Button
                        title="View"
                        size="sm"
                        className="me-1 btn-blue"
                        onClick={() => {
                          setSelectedLeadDiscussion(lead?.discussion),
                            setDisussionModal(true)
                        }}
                      >
                        <FaEye />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* ================= Pagination ================= */}
            <nav className="d-flex justify-content-between mt-3">
              <h6>
                Showing {filteredLeads.length === 0 ? 0 : start + 1} to{" "}
                {Math.min(start + pageSize, filteredLeads.length)} of{" "}
                {filteredLeads.length} entries
              </h6>

              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.max(prev - 1, 1))
                    }
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        <Modal
          isOpen={followUpModal}
          toggle={() => setFollowUpModal(false)}
          size="xl"
          centered
        >
          <ModalHeader toggle={() => setFollowUpModal(false)}>
            Lead Follow Up
          </ModalHeader>

          <ModalBody>
            {/* ================= Tabs ================= */}
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "add" })}
                  onClick={() => setActiveTab("add")}
                  style={{ cursor: "pointer" }}
                >
                  Add Follow Up
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "history" })}
                  onClick={() => setActiveTab("history")}
                  style={{ cursor: "pointer" }}
                >
                  History
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="mt-3">
              {/* ================= ADD FOLLOW UP ================= */}
              <TabPane tabId="add">
                <Form>
                  <Row>
                    <Col xl={6}>
                      <FormGroup>
                        <Label>
                          Follow Up Date <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="date"
                          name="date"
                          value={followUpForm.date}
                          onChange={handleFollowUpChange}
                          className="form-control rounded-4"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col xl={6}>
                      <FormGroup>
                        <Label>Next Follow Up Date</Label>
                        <Input
                          type="date"
                          name="nextFollowUpDate"
                          value={followUpForm.nextFollowUpDate}
                          onChange={handleFollowUpChange}
                          className="form-control rounded-4"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>
                      Description <span className="text-danger">*</span>{" "}
                    </Label>
                    <Input
                      type="textarea"
                      rows={10}
                      name="description"
                      value={followUpForm.description}
                      onChange={handleFollowUpChange}
                      className="form-control rounded-4"
                      required
                    />
                  </FormGroup>

                  <div className="d-flex justify-content-end">
                    <Button
                      type="button"
                      className="btn-blue"
                      onClick={saveFollowUp}
                    >
                      <MdAdd /> Follow Up
                    </Button>
                  </div>
                </Form>
              </TabPane>

              {/* ================= HISTORY ================= */}
              <TabPane tabId="history">
                {followUpHistory?.length === 0 ? (
                  <p className="text-muted text-center">
                    No follow up history available
                  </p>
                ) : (
                  <Table bordered striped>
                    <thead className="table-dark">
                      <tr>
                        <th>Follow Up Date</th>
                        <th>Next Follow Up</th>
                        <th>Description</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {followUpHistory.reverse()?.map(item => (
                        <tr key={item.id}>
                          <td>{moment(item.date).format("DD-MM-YYYY")}</td>
                          <td>
                            {item.nextFollowUpDate
                              ? moment(item.nextFollowUpDate).format(
                                  "DD-MM-YYYY"
                                )
                              : "-"}
                          </td>
                          <td
                            style={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {item.description}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => deleteFollowUp(item.id)}
                            >
                              <FaTrashAlt />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </TabPane>
            </TabContent>
          </ModalBody>
        </Modal>

        <Modal isOpen={disussionModal} centered>
          <ModalHeader toggle={() => setDisussionModal(false)}>
            Lead Dissussion
          </ModalHeader>
          <ModalBody>
            <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
              {selectedLeadDiscussion}
            </p>
          </ModalBody>
        </Modal>
      </div>
    </PermittedComponent>
  )
}

export default LeadsIndex
